/**
 * In-app paths of the TPIN-first screens (the route table, UX-T16 section 2.1).
 * One place, so a link and the router can never disagree. These are SPA paths,
 * not BFF routes: BFF route strings live only in the api layer.
 */

export function tpinPath(tin: string): string {
  return `/tpin/${encodeURIComponent(tin)}`
}

export function branchPath(tin: string, dbName: string): string {
  return `${tpinPath(tin)}/branch/${encodeURIComponent(dbName)}`
}

/** The branch-scoped failed list (S3), reached from a failed count (D-15). */
export function failedListPath(dbName: string): string {
  return `/deployment/${encodeURIComponent(dbName)}`
}

/** The branch-scoped pending list (S4). */
export function pendingListPath(dbName: string): string {
  return `${failedListPath(dbName)}/pending`
}

const TPIN_SCREEN_PATH = /^\/tpin\/([0-9]+)(?:\/branch\/([0-9]+_[0-9]+))?\/?$/

/**
 * The TPIN-first screen a path names: `/tpin/{tin}` is the overview (dbName null),
 * `/tpin/{tin}/branch/{dbName}` is one branch; any other path is not one of them.
 */
export function parseTpinPath(pathname: string): { readonly tin: string; readonly dbName: string | null } | null {
  const match = TPIN_SCREEN_PATH.exec(pathname)
  return match === null ? null : { tin: match[1] as string, dbName: match[2] ?? null }
}

/** The S5 invoice detail of one invoice in one branch (SIH-17 I-8c). */
export function invoicePath(tin: string, dbName: string, rectNo: number): string {
  return `${branchPath(tin, dbName)}/invoice/${rectNo}`
}

const INVOICE_SCREEN_PATH = /^\/tpin\/([0-9]+)\/branch\/([0-9]+_[0-9]+)\/invoice\/([0-9]+)\/?$/

/** The invoice an S5 path names, or null for any other path (the app's unknown-path rewrite then applies). */
export function parseInvoicePath(
  pathname: string,
): { readonly tin: string; readonly dbName: string; readonly rectNo: number } | null {
  const match = INVOICE_SCREEN_PATH.exec(pathname)
  if (match === null) return null
  return { tin: match[1] as string, dbName: match[2] as string, rectNo: Number(match[3]) }
}

/**
 * The TPIN a branch belongs to: the part of `{tin}_{branch}` before the
 * underscore. The list routes validate the shape (`^[0-9]+_[0-9]+$`) before a
 * page renders, so the underscore is always there.
 */
export function tinOfBranch(dbName: string): string {
  return dbName.split('_')[0] as string
}
